import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Btn, Field, TextInput } from "./primitives";
import { api, useUnits, formatApiError } from "../lib/providers";
import { toast } from "sonner";
import { X, Ruler } from "@phosphor-icons/react";

/**
 * Modal: create an Aufmaß directly from a quote.
 *
 * Pre-fills customer + site from the quote, lists all quote lines with checkboxes
 * (header checkbox toggles all). The user can optionally adjust quantity and unit
 * before creating the Aufmaß.
 */
export default function AufmassFromQuoteModal({ open, onClose, quote, lang = "de" }) {
  const navigate = useNavigate();
  const { units } = useUnits();
  const [selected, setSelected] = useState({}); // index -> bool
  const [overrides, setOverrides] = useState({}); // index -> { quantity, unit }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && quote) {
      // Default: select all lines
      const init = {};
      (quote.lines || []).forEach((_, i) => { init[i] = true; });
      setSelected(init);
      setOverrides({});
    }
  }, [open, quote]);

  const lines = quote?.lines || [];
  const allSelected = lines.length > 0 && lines.every((_, i) => selected[i]);
  const noneSelected = lines.every((_, i) => !selected[i]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(lines.map((_, i) => [i, true])));
  };

  const updateQty = (i, qty) => setOverrides({ ...overrides, [i]: { ...overrides[i], quantity: qty } });
  const updateUnit = (i, unit) => setOverrides({ ...overrides, [i]: { ...overrides[i], unit } });

  const selectedLines = useMemo(() => {
    return lines
      .map((l, i) => {
        if (!selected[i]) return null;
        const ov = overrides[i] || {};
        return {
          name: l.name || "",
          description: l.description || "",
          quantity: ov.quantity !== undefined && ov.quantity !== "" ? Number(ov.quantity) : Number(l.quantity || 0),
          unit: ov.unit || l.unit || "Stk",
        };
      })
      .filter(Boolean);
  }, [lines, selected, overrides]);

  const create = async () => {
    if (!quote || selectedLines.length === 0) return;
    setBusy(true);
    try {
      const payload = {
        title: quote.title || "",
        customer_id: quote.customer_id || null,
        customer_snapshot: quote.customer_snapshot || null,
        measurement_date: new Date().toISOString().slice(0, 10),
        site_address: "",
        notes: "",
        lines: selectedLines,
        source_quote_id: quote.id,
        source_quote_number: quote.number,
      };
      const { data } = await api.post("/aufmasse", payload);
      toast.success(lang === "de" ? "Aufmaß erstellt" : "Aufmaß created");
      onClose();
      navigate(`/aufmasse/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  if (!quote) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="aufmass-from-quote-overlay">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="aufmass-from-quote-modal">
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-overline inline-flex items-center gap-1.5">
              <Ruler size={12} /> {lang === "de" ? "Aufmaß aus Angebot" : "Aufmaß from quote"}
            </div>
            <h2 className="font-heading text-xl font-semibold mt-0.5">
              {quote.number}{quote.title ? ` – ${quote.title}` : ""}
            </h2>
            <div className="text-xs text-[var(--muted-2)] mt-1">
              {quote.customer_snapshot?.name || (lang === "de" ? "Kein Kunde" : "No customer")}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid="aufmass-from-quote-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-[var(--muted)] mb-4">
            {lang === "de"
              ? "Wähle die Positionen, die ins Aufmaß übernommen werden sollen. Mengen und Einheiten kannst du frei anpassen."
              : "Select lines to include. Quantities and units are freely adjustable."}
          </p>

          <div className="border border-[var(--border)] rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg)] text-[var(--muted)]">
                <tr>
                  <th className="w-10 px-3 py-2 text-left">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="aufmass-from-quote-select-all" />
                  </th>
                  <th className="text-left px-3 py-2 label-overline">{lang === "de" ? "Pos." : "Pos."}</th>
                  <th className="text-left px-3 py-2 label-overline">{lang === "de" ? "Beschreibung" : "Description"}</th>
                  <th className="text-right px-3 py-2 label-overline w-28">{lang === "de" ? "Menge" : "Qty"}</th>
                  <th className="text-left px-3 py-2 label-overline w-24">{lang === "de" ? "Einheit" : "Unit"}</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--muted-2)]">{lang === "de" ? "Keine Positionen im Angebot." : "No lines in quote."}</td></tr>
                )}
                {lines.map((l, i) => {
                  const isSel = !!selected[i];
                  const ov = overrides[i] || {};
                  return (
                    <tr key={i} className={`border-t border-[var(--border)] ${isSel ? "bg-[var(--brand-soft)]" : ""}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                          data-testid={`aufmass-from-quote-line-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-[var(--muted)] font-mono">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-[var(--ink)]">{l.name || "—"}</div>
                        {l.description && <div className="text-xs text-[var(--muted-2)] line-clamp-1">{l.description}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <TextInput
                          type="number"
                          step={(ov.unit || l.unit || "").toLowerCase() === "stk" ? "1" : "0.01"}
                          disabled={!isSel}
                          value={ov.quantity !== undefined ? ov.quantity : (isSel ? l.quantity : "")}
                          onChange={(e) => updateQty(i, e.target.value)}
                          className="text-right h-8"
                          data-testid={`aufmass-from-quote-qty-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          disabled={!isSel}
                          value={ov.unit || l.unit || "Stk"}
                          onChange={(e) => updateUnit(i, e.target.value)}
                          className="w-full h-8 px-2 bg-[var(--surface)] border border-[var(--border)] rounded-sm text-sm focus:border-[var(--brand)] focus:outline-none disabled:opacity-40"
                          data-testid={`aufmass-from-quote-unit-${i}`}
                        >
                          {(units || []).map((u) => <option key={u.value} value={u.value}>{u.value}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-between items-center">
          <span className="text-xs text-[var(--muted)]">
            {selectedLines.length}/{lines.length} {lang === "de" ? "Positionen ausgewählt" : "lines selected"}
          </span>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={onClose} data-testid="aufmass-from-quote-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Btn>
            <Btn onClick={create} disabled={busy || noneSelected} data-testid="aufmass-from-quote-submit">
              <Ruler size={16} /> {busy ? "…" : (lang === "de" ? "Aufmaß anlegen" : "Create Aufmaß")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
